import { Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';
import { FuncionesServiceProvider } from 'src/app/service/index.providers';
import { ThemeColorsService } from 'src/app/service/theme-colors/theme-colors.service';

/**
 * Reusable animated brand background.
 *
 * Renders an infinite horizontal-slide SVG (seamless loop via identical
 * copies) with an optional white fog/haze overlay. The shapes are painted with
 * CSS custom properties, so the palette is re-themed per brand from a config
 * object without touching the SVG or the animation.
 *
 * Usage:
 *   <app-brand-animated-bg></app-brand-animated-bg>                 // default palette
 *   <app-brand-animated-bg [brandConfig]="config"></app-brand-animated-bg>
 *   <app-brand-animated-bg [showFog]="false"></app-brand-animated-bg>
 *
 * The host positions itself (absolute, bottom of its offset parent). Give the
 * offset parent `position: relative; overflow: hidden` and place your content
 * above it with a higher z-index.
 */
@Component({
  selector: 'app-brand-animated-bg',
  templateUrl: './brand-animated-bg.component.html',
  styleUrls: ['./brand-animated-bg.component.scss'],
})
export class BrandAnimatedBgComponent implements OnInit, OnChanges {
  /** Brand config used to derive the palette. Falls back to the default theme. */
  @Input() brandConfig: any;
  /** Toggles the white fog/haze overlay on top of the animation. */
  @Input() showFog = true;

  /** Unique per-instance prefix so several backgrounds can coexist on one page. */
  private static instanceCount = 0;
  readonly uid = `brandBg${BrandAnimatedBgComponent.instanceCount++}`;

  @HostBinding('style.--login-bg-primary') primaryColor = '';
  @HostBinding('style.--login-bg-secondary') secondaryColor = '';
  @HostBinding('style.--login-bg-tertiary') tertiaryColor = '';
  @HostBinding('attr.aria-hidden') ariaHidden = 'true';

  private initialized = false;

  constructor(
    private fp: FuncionesServiceProvider,
    private themeColorsService: ThemeColorsService,
  ) {}

  ngOnInit() {
    this.applyColors();
    this.initialized = true;
  }

  ngOnChanges() {
    // ngOnChanges fires before ngOnInit; let ngOnInit handle the first pass and
    // only react here to later brandConfig updates.
    if (this.initialized) {
      this.applyColors();
    }
  }

  private applyColors() {
    const config = this.themeColorsService.defaultConfigData;
    // const config = this.brandConfig || this.themeColorsService.defaultConfigData;
    const colors = this.fp.getMultibrandBgColors(config);
    this.primaryColor = colors.primary;
    this.secondaryColor = colors.secondary;
    this.tertiaryColor = colors.tertiary;
  }
}
